import Tippy, { TippyProps } from "@tippyjs/react";
import React from "react";
import "./Tooltip.css";
import { useTooltipGroup } from "./TooltipGroup";

/**
 * Placement options for the tooltip relative to the target element
 * Based on Tippy.js core documentation
 */
export type Placement =
  | "top"
  | "top-start"
  | "top-end"
  | "right"
  | "right-start"
  | "right-end"
  | "bottom"
  | "bottom-start"
  | "bottom-end"
  | "left"
  | "left-start"
  | "left-end"
  | "auto"
  | "auto-start"
  | "auto-end";

/**
 * Props for the Tooltip component
 * Extends Tippy.js props with customized options
 */
export interface TooltipProps
  extends Omit<
    TippyProps,
    | "children"
    | "className"
    | "delay"
    | "duration"
    | "placement"
    | "offset"
    | "trigger"
  > {
  /**
   * Target element that triggers the tooltip
   * Must be a valid React element that can accept ref forwarding
   */
  children: React.ReactElement;

  /**
   * Delay in ms before showing/hiding the tooltip
   * Can be a single number or [show delay, hide delay]
   * @default 400
   */
  delay?: number | [number | null, number | null];

  /**
   * Event(s) that trigger the tooltip
   * @default "mouseenter focus"
   */
  trigger?: string;

  /**
   * Duration in ms for the show/hide animation
   * Can be a single number or [show duration, hide duration]
   * @default [275, 0]
   */
  duration?: number | [number | null, number | null];

  /**
   * Placement of the tooltip relative to the target element
   * @default "bottom-end"
   */
  placement?: Placement;

  /**
   * Offset of the tooltip from the target element
   * Format is [skidding, distance] in pixels
   * @default [0, 0]
   */
  offset?: [number, number];

  /**
   * Optional variant for styling
   * @default "default"
   */
  variant?: "default" | "light" | "dark";

  /**
   * Optional size variant
   * @default "md"
   */
  size?: "sm" | "md" | "lg";
}

/**
 * Tooltip Component
 *
 * A lightweight, flexible tooltip component based on Tippy.js
 * that displays informational text when users hover over or focus
 * on an element.
 *
 * Features:
 * - Multiple placement options
 * - Customizable delay and duration
 * - Adjustable offset
 * - Multiple visual variants
 * - Accessibility support
 *
 * @example
 * // Basic usage
 * <Tooltip content="This is a tooltip">
 *   <button>Hover me</button>
 * </Tooltip>
 *
 * @example
 * // With custom placement and timing
 * <Tooltip
 *   content="Tooltip with custom settings"
 *   placement="top"
 *   delay={200}
 *   duration={[200, 100]}
 * >
 *   <span>Hover for tooltip</span>
 * </Tooltip>
 *
 * @example
 * // With different variants
 * <Tooltip content="Light tooltip" variant="light">
 *   <button>Light</button>
 * </Tooltip>
 */
export const Tooltip: React.FC<TooltipProps> = ({
  children,
  content,
  delay = 400,
  duration = [275, 0],
  placement = "bottom-end",
  offset = [0, 0],
  variant = "default",
  size = "md",
  trigger = "mouseenter focus",
  visible,
  ...rest
}) => {
  const groupContext = useTooltipGroup();

  // When inside a TooltipGroup, use the singleton target
  // The group provides shared delay, duration, and styling
  // Individual tooltips can still override content, placement, and offset
  if (groupContext) {
    return (
      <Tippy
        content={content}
        placement={placement}
        offset={offset}
        singleton={groupContext.target}
        {...rest}
      >
        {children}
      </Tippy>
    );
  }

  // Construct the className based on variant and size (only for standalone tooltips)
  const tooltipClassName = [
    "indigo-tooltip",
    `indigo-tooltip-${variant}`,
    `indigo-tooltip-${size}`,
  ].join(" ");

  // Standalone tooltip - don't pass trigger in controlled mode (when visible is defined)
  const tippyProps = {
    content,
    delay,
    duration,
    placement,
    offset,
    className: tooltipClassName,
    ...(visible !== undefined ? { visible } : { trigger }),
    ...rest,
  };

  return <Tippy {...tippyProps}>{children}</Tippy>;
};

export default Tooltip;
